import { Auth, I18n } from 'aws-amplify';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';

import { SharpOutlinedTextField } from 'src/legacy/components/TextField';
import { getCookie } from 'src/utils/StorageUtils';

import OnboardingButton from '../../OnboardingButton';
import { getFormErrorText } from '../../utils';
import { OnboardingFormError } from '../OnboardingFormError';

type AccountFormProps = {
  onSuccess: (deliveryMedium: string, email: string) => void;
};

export const AccountForm: React.FC<AccountFormProps> = ({ onSuccess }) => {
  const [isClient, setIsClient] = useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const handleSendCode = async (values: { username: string }) => {
    const { username: inputUsername } = values;
    if (!inputUsername) return;
    const username = inputUsername.toLowerCase();
    try {
      // Skip Cognito's forgotPassword API when running in Reflect e2e tests
      // to avoid hitting Cognito's built-in rate limit across test runs.
      if (getCookie('__disable_animations')) {
        onSuccess('EMAIL', username);
        return;
      }

      const data = await Auth.forgotPassword(username);

      if (!data) return;
      onSuccess(data?.CodeDeliveryDetails?.DeliveryMedium, username);
    } catch (error) {
      setErrorMessage(
        'We encountered an issue resetting your password. Please check your email address and try again, or contact support if the problem persists.',
      );
    }
  };

  useEffect(() => {
    setIsClient(true);
  }, [isClient === false]);

  return (
    <Formik
      initialValues={{
        username: '',
      }}
      validationSchema={Yup.object().shape({
        username: Yup.string()
          .email('Invalid email')
          .max(100)
          .required(
            // API errors take priority over schema validation errors.
            errorMessage || 'Email is required',
          ),
      })}
      onSubmit={async (values, { resetForm }) => {
        try {
          await handleSendCode(values);
          resetForm();
        } catch (error) {
          resetForm();
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleSubmit,
        touched,
        handleChange,
        isSubmitting,
      }) => (
        <form noValidate onSubmit={handleSubmit}>
          <SharpOutlinedTextField
            fullWidth
            sizeVariant="tall"
            type="text"
            key={`username-${isClient ? 0 : 1}`}
            name="username"
            variant="outlined"
            onBlur={handleBlur}
            onInput={(e) => {
              setErrorMessage(''); // clear the Auth error when user starts typing again
              handleChange(e);
            }}
            label={I18n.get('Email')}
            placeholder={I18n.get('Email')}
            error={Boolean(
              (touched.username && errors.username) || errorMessage,
            )}
            autoComplete="off"
            autoFocus
            inputProps={{
              'data-testid': 'email-input',
            }}
          />
          <OnboardingFormError
            errorText={getFormErrorText(errors, touched) || errorMessage}
          />
          <OnboardingButton
            size="large"
            fullWidth
            htmlId="forgot-password-send-code"
            type="submit"
            color="primary"
            variant="contained"
            isLoading={isSubmitting}
            data-testid="submit-button"
          >
            {I18n.get('Next')}
          </OnboardingButton>
        </form>
      )}
    </Formik>
  );
};
